// pages/ForgotPassword.tsx
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import DOMPurify from 'dompurify';
import useAdmin from '../hooks/useAdmin';
import { Link } from 'react-router-dom';
import ErrorDisplay from '../../shared/components/ErrorDisplay';

interface ForgotPasswordForm {
  email: string;
}

export default function ForgotPassword() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const { refreshCsrf, error, setError } = useAdmin();
  const { register, handleSubmit, formState: { errors } } = useForm<ForgotPasswordForm>();

  const onSubmit = async (data: ForgotPasswordForm) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const csrfToken = await refreshCsrf();
      const res = await fetch('/admin/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || '',
        },
        credentials: 'include',
        body: JSON.stringify({ email: DOMPurify.sanitize(data.email || "") }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to send reset link');
      setSuccess('Reset link sent to your email.');
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-800 to-pink-700 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">Forgot Password</h2>

        {error && <ErrorDisplay error={error} onDismiss={() => setError('')} />}

        {success && (
          <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg">
            {success}
            <div className="mt-2 text-center">
              <Link to="/admin/login" className="text-purple-600 hover:underline">
                Back to Login
              </Link>
            </div>
          </div>
        )}

        {!success && (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <input type="hidden" name="_csrf" />
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                placeholder="Enter your email"
                {...register('email', {
                  required: 'Email required',
                  pattern: { value: /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i, message: 'Invalid email' },
                })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                aria-invalid={!!errors.email}
              />
              {errors.email && <p className="text-red-600 text-sm">{errors.email.message}</p>}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition transform hover:scale-105 disabled:opacity-70"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
        )}

        {!success && (
          <p className="mt-4 text-center text-gray-600 text-sm">
            <Link to="/admin/login" className="text-purple-600 hover:underline">
              Back to Login
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}